import * as React from "react";
import { Trash2, ExternalLink, AlertCircle, CheckCircle2, Clock, XCircle, Mail, AlertTriangle, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getCountryName } from "@/lib/countries";

const DATABRICKS_URL = process.env.REACT_APP_DATABRICKS_URL || "https://dbc-365fb0d3-124f.cloud.databricks.com";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: {
      label: "Activa",
      variant: "default",
      className: "bg-emerald-100 text-emerald-700 border-emerald-200",
      icon: CheckCircle2,
    },
    pending: {
      label: "Pendiente",
      variant: "secondary",
      className: "bg-amber-100 text-amber-700 border-amber-200",
      icon: Clock,
    },
    pending_warehouse: {
      label: "Sin Warehouse",
      variant: "secondary",
      className: "bg-amber-100 text-amber-700 border-amber-200",
      icon: Clock,
    },
    databricks_error: {
      label: "Error",
      variant: "destructive",
      className: "bg-red-100 text-red-700 border-red-200",
      icon: XCircle,
    },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={`${config.className} gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export const AlertsList = ({ alerts, onDeleteAlert, isDeleting, currentUser }) => {
  const isAdmin = currentUser?.role === "admin";
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCountries = (countries) => {
    if (countries.length <= 3) {
      return countries.map((c) => `${getCountryName(c)} (${c})`).join(", ");
    }
    return `${countries.slice(0, 2).map((c) => getCountryName(c)).join(", ")} +${countries.length - 2} más`;
  };

  const formatSchedule = (interval) => {
    const scheduleLabels = {
      "1h": "Cada hora",
      "6h": "Cada 6 horas",
      "daily": "Diario",
      "weekly": "Semanal",
    };
    return scheduleLabels[interval] || interval || "Cada hora";
  };

  if (alerts.length === 0) {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="py-12">
          <div className="text-center text-slate-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
            <p className="text-lg font-medium text-slate-600">No hay alertas configuradas</p>
            <p className="text-sm mt-1">Crea tu primera alerta usando el formulario de arriba</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50">
        <CardTitle className="text-lg font-semibold tracking-tight">
          Alertas Configuradas
        </CardTitle>
        <CardDescription className="text-slate-500">
          {alerts.length} alerta{alerts.length !== 1 ? "s" : ""} en el sistema
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Nombre
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Cliente
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Países Autorizados
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Frecuencia
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Notificaciones
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Estado
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Creada
                </TableHead>
                {isAdmin && (
                  <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    Creador
                  </TableHead>
                )}
                <TableHead className="text-xs font-medium uppercase tracking-wider text-slate-500 text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow 
                  key={alert.id} 
                  className="hover:bg-slate-50/50"
                  data-testid={`alert-row-${alert.id}`}
                >
                  <TableCell className="font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      {alert.alert_name}
                      {alert.databricks_alert_id && (
                        <a
                          href={`${DATABRICKS_URL}/sql/alerts/${alert.databricks_alert_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-slate-600"
                          title="Ver en Databricks"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{alert.client_name}</TableCell>
                  <TableCell className="text-slate-600">
                    <span title={alert.authorized_countries.map((c) => `${getCountryName(c)} (${c})`).join(", ")}>
                      {formatCountries(alert.authorized_countries)}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-slate-400" />
                      {formatSchedule(alert.schedule_interval)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {alert.notification_emails && alert.notification_emails.length > 0 ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1.5">
                              {alert.notification_status === "configured" ? (
                                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1 cursor-help">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Activo
                                </Badge>
                              ) : alert.notification_status === "error" ? (
                                <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200 gap-1 cursor-help">
                                  <XCircle className="h-3 w-3" />
                                  Error
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 gap-1 cursor-help">
                                  <Mail className="h-3 w-3" />
                                  {alert.notification_emails.length}
                                </Badge>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="text-xs space-y-2">
                              <div className="font-semibold">Notificaciones por email:</div>
                              {alert.notification_emails.map((email, i) => (
                                <div key={i}>• {email}</div>
                              ))}
                              {alert.notification_status === "configured" && (
                                <div className="pt-2 border-t border-slate-600 mt-2">
                                  <span className="text-emerald-400 font-medium">✓ Configurado automáticamente</span>
                                  <p className="mt-1">Los emails recibirán notificaciones cuando la alerta se dispare.</p>
                                  {alert.databricks_job_id && (
                                    <p className="mt-1">
                                      <a 
                                        href={`${DATABRICKS_URL}/#job/${alert.databricks_job_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:underline"
                                      >
                                        Ver Job en Databricks →
                                      </a>
                                    </p>
                                  )}
                                </div>
                              )}
                              {alert.notification_status === "error" && (
                                <div className="pt-2 border-t border-slate-600 mt-2 text-red-400">
                                  Error al configurar notificaciones automáticas.
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-slate-400 text-xs">Sin configurar</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={alert.status} />
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {formatDate(alert.created_at)}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-slate-500 text-sm">
                      {alert.created_by_email ? (
                        <span className="truncate max-w-[150px] block" title={alert.created_by_email}>
                          {alert.created_by_email.split("@")[0]}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">-</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-alert-${alert.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar alerta?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción eliminará la alerta "{alert.alert_name}" tanto de este sistema como de Databricks. Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDeleteAlert(alert.id)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`confirm-delete-${alert.id}`}
                          >
                            {isDeleting ? "Eliminando..." : "Eliminar"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
